import { memo, useCallback, useState } from "react";
import { AnimatePresence } from "motion/react";
import { SquareMinus, SquarePlus } from "lucide-react";

import { motion } from "motion/react";
import styles from "./Sidebar.module.css";

type ListOptionsData = {
  id: string;
  name: string;
  children?: ListOptionsData[];
};

interface ListOptionsProps {
  name: string;
  onChange: (id: string, parentId: string | null) => void;
  selectedValues: string[] | undefined;
  data: ListOptionsData[];
  disableValues?: boolean;
}

const ListOptions = memo(({ name, data, onChange, selectedValues, disableValues }: ListOptionsProps) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const handleExpand = useCallback((id: string, parentId: string | null) => {
    setExpanded((prev) => {
      const next = new Set(prev);

      if (next.has(id) && !parentId) {
        return new Set();
      }

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  return (
    <div className={styles.section}>
      <div className={styles.label}>{name}</div>
      {data.map((item) => {
        return (
          <ListOptionsNode
            key={item.name}
            data={item}
            selectedValues={selectedValues}
            expanded={expanded}
            parentId={null}
            onSelect={onChange}
            onExpand={handleExpand}
            disableValues={disableValues}
          />
        );
      })}
    </div>
  );
});

interface ListOptionsNodeProps {
  data: ListOptionsData;
  selectedValues: string[] | undefined;
  expanded: Set<string>;
  parentId: string | null;
  disableValues?: boolean;
  onSelect: (id: string, parentId: string | null) => void;
  onExpand: (id: string, parentId: string | null, previousSelected: boolean) => void;
}

const ListOptionsNode = memo(
  ({ data, selectedValues, expanded, parentId, onSelect, onExpand, disableValues }: ListOptionsNodeProps) => {
    const isSelected = selectedValues?.includes(data.id) ? true : false;
    const isExpanded = expanded.has(data.id);

    const allChildren = (data.children || []).filter((child) => child.name !== data.name);
    const hasChildren = allChildren.length > 0;

    return (
      <div>
        <div className={styles.listOptionNode}>
          <button
            className={[styles.item, isSelected ? styles.itemActive : "", disableValues ? styles.itemDisabled : ""]
              .filter(Boolean)
              .join(" ")}
            key={data.name}
            onClick={() => onSelect(data.id, parentId)}
            disabled={disableValues}
          >
            <span className={styles.itemText}>{data.name}</span>
          </button>
          {hasChildren && (
            <div
              role="button"
              className={styles.listOptionToggle}
              onClick={() => onExpand(data.id, parentId, isSelected)}
            >
              {hasChildren && isExpanded && <SquareMinus size={18} />}
              {hasChildren && !isExpanded && <SquarePlus size={18} />}
            </div>
          )}
        </div>
        <AnimatePresence>
          {isExpanded && data.children && data.children?.length && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: expanded ? "auto" : 0, opacity: expanded ? 1 : 0 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={styles.subItemsContainer}
            >
              {data.children?.map((subItem) => {
                return (
                  <ListOptionsNode
                    key={subItem.name}
                    selectedValues={selectedValues}
                    data={subItem}
                    onSelect={onSelect}
                    onExpand={onExpand}
                    expanded={expanded}
                    parentId={data.id}
                    disableValues={disableValues}
                  />
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

export default ListOptions;
